import { useTranslation } from 'next-i18next'
import { useState } from 'react'
import { v4 as uuid } from 'uuid'

import ActivityWrapper from '~/bootcamp/Activities/components/ActivityWrapper'
import { ActivityProps } from '~/bootcamp/Activities/types/activities.types'
import AlgebraActivityWrapper from '~/bootcamp/Activities/Algebra/Components/AlgebraActivityWrapper'
import Equals from '~/bootcamp/Activities/components/Equals'
import Input from '~/bootcamp/Activities/components/Input'
import { hocOnEnterPress } from '~/bootcamp/Activities/utils/activities.utils'
import { useEmitInstance } from '~/bootcamp/Activities/utils/instance.utils'

import useMultCoefficient1 from './useMultCoefficient1'

function MultCoefficient1({
  answerState,
  actionButtonsFC,
  difficultyLevel,
  onAnswerStateChange,
  onInstanceChange,
  onNextQuestionClick,
  autofocus = true,
}: ActivityProps): React.ReactElement {
  const { t } = useTranslation('generics')
  const {
    integers,
    coefficient,
    variable,
    formValid,
    inputRef,
    answer,
    onInputChange,
    handleCheckAnswer,
    goToNextQuestion,
  } = useMultCoefficient1({
    difficultyLevel,
    onAnswerStateChange,
    onNextQuestionClick,
    answerState,
    autofocus,
  })

  useEmitInstance(
    onInstanceChange,
    integers.length > 0
      ? `${integers
          .map((integer, index) => (integer < 0 && index > 0 ? `(${integer})` : `${integer}`))
          .join(' \\cdot ')} \\cdot ${coefficient < 0 ? '(-' : ''}${
          Math.abs(coefficient) !== 1 ? Math.abs(coefficient) : ''
        }${variable}${coefficient < 0 ? ')' : ''} = \\square ${variable}`
      : null,
  )

  const [inputKey, setInputKey] = useState<string>(uuid())

  const handleGoToNextQuestion = (): void => {
    goToNextQuestion()
    setInputKey(uuid())
  }

  const onEnterPress = (): void => {
    if (formValid) {
      hocOnEnterPress(answerState, handleCheckAnswer, handleGoToNextQuestion)
    }
  }

  return (
    <ActivityWrapper>
      <AlgebraActivityWrapper className="flex-col">
        <div className="flex flex-row flex-wrap justify-center items-center mt-2">
          {integers.map((integer, index) => (
            <span key={integer}>
              {integer < 0 && index > 0 ? `(${integer})` : integer}
              <span className="mx-2">×</span>
            </span>
          ))}
          {coefficient < 0 && '(-'}
          {Math.abs(coefficient) !== 1 && Math.abs(coefficient)}
          <i>{variable}</i>
          {coefficient < 0 && ')'}
          <span className="block sm:inline-block">
            <Equals />
            <Input
              aria-label={t('ariaLabelButtons.insertCoefficientValue')}
              autoFocus={autofocus}
              isError={answerState === false}
              key={inputKey}
              maxDigits={4}
              onChange={(value) => onInputChange(value)}
              onEnter={onEnterPress}
              ref={inputRef}
              type="number"
              value={answer}
            />
            <i className="ml-1">{variable}</i>
          </span>
        </div>
      </AlgebraActivityWrapper>
      {actionButtonsFC &&
        actionButtonsFC({
          formValid,
          correctAnswer: answerState ?? false,
          submitAnswerAction: handleCheckAnswer,
          clearFormAction: () => onInputChange('clear'),
          goToNextQuestionAction: handleGoToNextQuestion,
        })}
    </ActivityWrapper>
  )
}

export default MultCoefficient1
